import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';
import { TooltipContent, TooltipRoot, TooltipTrigger } from '@/components/ui/tooltip';
import { useTutorialMode } from '@/contexts/TutorialModeContext';
import { cn } from '@/lib/utils/classNames';

/** Natively interactive elements keep their default cursor;
 *  only passive/informational triggers get a help cursor. */
function isInteractiveTrigger(child: ReactElement): boolean {
  return child.type === 'a' || child.type === 'button';
}

type TooltipSide = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  content: ReactNode;
  children: ReactElement;
  /** 기존 API 호환용. 내부적으로 Radix side로 매핑된다. */
  placement?: TooltipSide;
  side?: TooltipSide;
  /** 기존 API 호환용. */
  delay?: number;
  delayDuration?: number;
  className?: string;
  /** Arrow(화살표) 색상. 밝은 배경 시 'fill-white' 등 지정 */
  arrowClassName?: string;
  /** Radix 충돌 감지 비활성화. placement를 강제 고정할 때 false로 지정 */
  avoidCollisions?: boolean;
  /**
   * Essential tooltips (delete/irreversible-action warnings, payment
   * amount/settlement info) stay visible even when Tutorial Mode is OFF.
   * Defaults to false — a normal guidance tooltip that Tutorial Mode hides.
   */
  essential?: boolean;
}

/**
 * 프로젝트 전역 툴팁 진입점.
 * 기존 `placement`/`delay` 호출부와 shadcn 기반 `side`/`delayDuration` 호출부를 함께 지원한다.
 */
export default function Tooltip({
  content,
  children,
  placement,
  side,
  delay,
  delayDuration,
  className,
  arrowClassName,
  avoidCollisions,
  essential,
}: TooltipProps) {
  const tutorialMode = useTutorialMode();

  if (!content) return children;

  const childProps = isValidElement(children)
    ? (children.props as { disabled?: boolean; className?: string })
    : undefined;

  // Disabled controls keep their tooltip even in Tutorial Mode OFF: it almost
  // always explains WHY the control is disabled, which is guidance the user
  // still needs — not dismissible tutorial hinting.
  const keepWhenTutorialOff = essential || childProps?.disabled === true;

  // Tutorial Mode OFF hides non-essential guidance tooltips app-wide.
  // Render the bare children (no help cursor, no wrapper) so nothing signals
  // a tooltip that won't open.
  if (tutorialMode === 'off' && !keepWhenTutorialOff) return children;

  const resolvedSide = side ?? placement ?? 'top';
  const resolvedDelay = delayDuration ?? delay ?? 0;
  let triggerChild: ReactElement = children;
  if (childProps?.disabled) {
    triggerChild = <span className="inline-flex cursor-help">{children}</span>;
  } else if (isValidElement(children) && !isInteractiveTrigger(children)) {
    // Add help cursor automatically so passive tooltipped data is discoverable.
    // Anchors / buttons keep their native cursor (they're clickable).
    triggerChild = cloneElement(children as ReactElement<{ className?: string }>, {
      className: cn(childProps?.className, ''),
    });
  }

  // Optional (tutorial-only) tooltips get a muted light-gray surface (bordered,
  // dark-gray text) so a dismissible hint is visually separable from an always-on
  // tooltip (which keeps the default dark surface with white text). Keyed off the
  // *effective* always-on state, so a disabled control's reason tooltip reads as
  // essential (dark), not tutorial. Callers that pass their own `className`/
  // `arrowClassName` still win — the variant is layered underneath.
  // White surface with a strong shadow (no border — the Radix arrow can't carry
  // one, which looks detached). The deeper shadow separates the light tooltip
  // from the page; the arrow fill stays white to match the surface.
  // `!` on the shadow: the base's custom `shadow-ggc-md` token isn't in the same
  // tailwind-merge group as an arbitrary shadow, so it wouldn't be deduped —
  // important makes the stronger tooltip shadow win deterministically.
  const variantClassName = keepWhenTutorialOff
    ? undefined
    : 'bg-white text-ggc-text-gray !shadow-[0_4px_16px_rgba(12,17,28,0.22)]';
  const variantArrowClassName = keepWhenTutorialOff ? undefined : 'fill-white';

  return (
    <TooltipRoot delayDuration={resolvedDelay}>
      <TooltipTrigger asChild>{triggerChild}</TooltipTrigger>
      <TooltipContent
        side={resolvedSide}
        className={cn(variantClassName, className)}
        arrowClassName={arrowClassName ?? variantArrowClassName}
        {...(avoidCollisions !== undefined ? { avoidCollisions } : {})}
      >
        {content}
      </TooltipContent>
    </TooltipRoot>
  );
}
